/**
 * Apply BannerSettings -> CSS variables on the shadow root.
 * Naming convention: --csteer_* (underscore, matches the legacy convention).
 *
 * Anything that isn't supplied by the server keeps the default from
 * styles.css. This makes the theme additive: server overrides defaults.
 */

import { resolveAssetUrl } from "./assets.js";
import type { BannerColors, BannerSettings } from "./types.js";

type CssVars = Record<string, string>;
type ForceScheme = "light" | "dark" | undefined;

/**
 * Self-hosted webfonts (served from our own /fonts/ origin, NOT Google Fonts,
 * so the visitor's IP never reaches a third party). When one of these fonts is
 * selected we inject @font-face rules into document.head on demand. @font-face
 * declared at document level applies inside shadow roots too (font resolution
 * is global), so the banner's shadow DOM picks it up.
 */
const WEBFONTS: Record<string, { family: string; faces: { weight: number; file: string }[] }> = {
  lato: {
    family: "Lato",
    faces: [
      { weight: 400, file: "/fonts/lato-400.woff2" },
      { weight: 700, file: "/fonts/lato-700.woff2" },
    ],
  },
  roboto: {
    family: "Roboto",
    faces: [
      { weight: 400, file: "/fonts/roboto-400.woff2" },
      { weight: 700, file: "/fonts/roboto-700.woff2" },
    ],
  },
};

const injectedFonts = new Set<string>();

/** Inject @font-face for a self-hosted webfont key (idempotent, no-op otherwise). */
function ensureWebFont(key: string): void {
  if (typeof document === "undefined" || injectedFonts.has(key)) return;
  const font = WEBFONTS[key];
  if (!font) return;
  injectedFonts.add(key);
  const css = font.faces
    .map(
      (f) =>
        `@font-face{font-family:"${font.family}";font-style:normal;font-weight:${f.weight};font-display:swap;src:url("${resolveAssetUrl(f.file)}") format("woff2");}`,
    )
    .join("");
  const style = document.createElement("style");
  style.setAttribute("data-csteer-font", key);
  style.textContent = css;
  document.head.appendChild(style);
}

/** Tracks the prefers-color-scheme listener per host so re-applying (or a
 *  banner re-mount) never stacks duplicate listeners or leaks the old one. */
const schemeListeners = new WeakMap<HTMLElement, () => void>();

/**
 * Resolve which colour palette to use given the theme mode and the visitor's
 * environment (or a forced scheme in preview). Falls back to the light
 * `colors` whenever a dark palette is requested but not configured.
 */
function resolveColors(settings: BannerSettings, force: ForceScheme): BannerColors {
  const mode = settings.themeMode ?? "light";
  const dark = settings.colorsDark;
  if (mode === "dark") return dark ?? settings.colors;
  if (mode === "auto") {
    const prefersDark =
      force === "dark" ||
      (force !== "light" &&
        typeof window !== "undefined" &&
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-color-scheme: dark)").matches);
    return prefersDark ? (dark ?? settings.colors) : settings.colors;
  }
  return settings.colors;
}

export function applyTheme(host: HTMLElement, settings: BannerSettings, force?: ForceScheme): void {
  // Load the selected webfont (Lato/Roboto…) if needed — no-op for system fonts.
  ensureWebFont(settings.layout.fontFamily ?? "system");

  // Tear down any previous auto-mode listener bound to this host.
  schemeListeners.get(host)?.();
  schemeListeners.delete(host);

  const writeColorVars = () => {
    const vars = buildColorVars(resolveColors(settings, force));
    for (const [name, value] of Object.entries(vars)) {
      host.style.setProperty(name, value);
    }
  };
  writeColorVars();

  // Layout/position vars don't depend on the palette — set once.
  const layoutVars = buildLayoutVars(settings);
  for (const [name, value] of Object.entries(layoutVars)) {
    host.style.setProperty(name, value);
  }

  // In auto mode (without a forced scheme), live-update the palette if the
  // visitor toggles their OS theme while the banner is open.
  if (
    (settings.themeMode ?? "light") === "auto" &&
    settings.colorsDark &&
    !force &&
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function"
  ) {
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => writeColorVars();
    mql.addEventListener("change", onChange);
    schemeListeners.set(host, () => mql.removeEventListener("change", onChange));
  }
}

function buildColorVars(colors: BannerColors): CssVars {
  const vars: CssVars = {
    "--csteer_color_background": colors.background,
    "--csteer_color_foreground": colors.foreground,
    "--csteer_color_border": colors.border,
    "--csteer_color_accent": colors.accent,
    "--csteer_color_accent_foreground": colors.accentForeground,
    "--csteer_color_accent_hover": colors.accentHover,
  };
  // Per-button overrides (all optional). Refuse falls back to an outline style,
  // customize mirrors refuse, save mirrors accept — handled by styles.css.
  if (colors.refuseBackground) vars["--csteer_refuse_background"] = colors.refuseBackground;
  if (colors.refuseForeground) vars["--csteer_refuse_foreground"] = colors.refuseForeground;
  if (colors.refuseBackgroundHover)
    vars["--csteer_refuse_background_hover"] = colors.refuseBackgroundHover;
  if (colors.customizeBackground)
    vars["--csteer_customize_background"] = colors.customizeBackground;
  if (colors.customizeForeground)
    vars["--csteer_customize_foreground"] = colors.customizeForeground;
  if (colors.customizeBackgroundHover)
    vars["--csteer_customize_background_hover"] = colors.customizeBackgroundHover;
  if (colors.saveBackground) vars["--csteer_save_background"] = colors.saveBackground;
  if (colors.saveForeground) vars["--csteer_save_foreground"] = colors.saveForeground;
  if (colors.saveBackgroundHover)
    vars["--csteer_save_background_hover"] = colors.saveBackgroundHover;
  return vars;
}

/**
 * GDPR-safe font stacks (mirror of @consenteer/shared FONT_STACKS — duplicated
 * here because the SDK has no runtime dependency on @consenteer/shared).
 */
const FONT_STACKS: Record<string, string> = {
  system: 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  inherit: "inherit",
  arial: 'Arial, "Helvetica Neue", Helvetica, sans-serif',
  verdana: "Verdana, Geneva, Tahoma, sans-serif",
  trebuchet: '"Trebuchet MS", "Segoe UI", Tahoma, sans-serif',
  georgia: 'Georgia, "Times New Roman", serif',
  times: '"Times New Roman", Times, serif',
  courier: 'ui-monospace, "Courier New", Courier, monospace',
  lato: '"Lato", system-ui, -apple-system, sans-serif',
  roboto: '"Roboto", system-ui, -apple-system, sans-serif',
};

function buildLayoutVars(settings: BannerSettings): CssVars {
  const { layout, reopenIcon } = settings;
  const vars: CssVars = {};

  // Typography: font family (resolved from a key) + button text size.
  const fontKey = layout.fontFamily ?? "system";
  vars["--csteer_font_family"] =
    FONT_STACKS[fontKey] ?? "system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif";
  if (layout.buttonFontSize) vars["--csteer_button_font_size"] = layout.buttonFontSize;
  // Per-button font-size overrides (each falls back to --csteer_button_font_size
  // via the CSS var fallback in styles.css).
  const sizes = layout.buttonFontSizes;
  if (sizes?.accept) vars["--csteer_accept_font_size"] = sizes.accept;
  if (sizes?.refuse) vars["--csteer_refuse_font_size"] = sizes.refuse;
  if (sizes?.customize) vars["--csteer_customize_font_size"] = sizes.customize;
  if (sizes?.save) vars["--csteer_save_font_size"] = sizes.save;

  // Reopen icon — size + corner position + wedge color. The host is
  // shared with the banner host's theme call, so these vars are also
  // harmlessly defined on the banner host; the rule scopes by class so
  // they only apply on .csteer_reopen.
  if (reopenIcon) {
    vars["--csteer_reopen_wedge_background"] = reopenIcon.wedgeBackground;
    vars["--csteer_reopen_size"] = `${reopenIcon.size}px`;
    const reset = "auto";
    vars["--csteer_reopen_top"] = reset;
    vars["--csteer_reopen_bottom"] = reset;
    vars["--csteer_reopen_left"] = reset;
    vars["--csteer_reopen_right"] = reset;
    switch (reopenIcon.position) {
      case "bottom-right":
        vars["--csteer_reopen_bottom"] = "var(--csteer_reopen_offset)";
        vars["--csteer_reopen_right"] = "var(--csteer_reopen_offset)";
        break;
      case "bottom-left":
        vars["--csteer_reopen_bottom"] = "var(--csteer_reopen_offset)";
        vars["--csteer_reopen_left"] = "var(--csteer_reopen_offset)";
        break;
      case "top-right":
        vars["--csteer_reopen_top"] = "var(--csteer_reopen_offset)";
        vars["--csteer_reopen_right"] = "var(--csteer_reopen_offset)";
        break;
      case "top-left":
        vars["--csteer_reopen_top"] = "var(--csteer_reopen_offset)";
        vars["--csteer_reopen_left"] = "var(--csteer_reopen_offset)";
        break;
    }
  }

  if (layout.width) vars["--csteer_banner_width"] = layout.width;
  if (layout.borderRadius) vars["--csteer_banner_border_radius"] = layout.borderRadius;

  // Full-width families (banner/pushdown): edge-to-edge by default; when
  // layout.maxWidth is set, the strip is centered and capped at that width
  // on desktop (the 100vw cap in styles.css keeps mobile edge-to-edge) and
  // the user's border radius applies again.
  const fullWidthCap = () => {
    vars["--csteer_banner_width"] = "100%";
    if (layout.maxWidth) {
      vars["--csteer_banner_max_width"] = layout.maxWidth;
      vars["--csteer_banner_border_radius"] = layout.borderRadius || "0";
    } else {
      vars["--csteer_banner_max_width"] = "100vw";
      vars["--csteer_banner_border_radius"] = "0";
    }
  };

  // Position: only one of the four offsets is meaningful for each preset.
  const reset = "auto";
  vars["--csteer_banner_top"] = reset;
  vars["--csteer_banner_bottom"] = reset;
  vars["--csteer_banner_left"] = reset;
  vars["--csteer_banner_right"] = reset;
  // Identity, NOT `none`: the entrance/exit keyframes compose this variable
  // with their own translate/scale, and `transform: none translate(...)` is
  // invalid CSS — the browser drops the whole declaration. That silently
  // reduced the animation to a plain fade on every format that used to reset
  // to `none` (box, sidebar, banner); only the two centred presets, which set
  // a real translate, ever animated as intended.
  vars["--csteer_banner_transform"] = "translate(0)";

  switch (layout.position) {
    // --- box family -------------------------------------------------------
    case "bottom-right":
      vars["--csteer_banner_bottom"] = "16px";
      vars["--csteer_banner_right"] = "16px";
      break;
    case "bottom-left":
      vars["--csteer_banner_bottom"] = "16px";
      vars["--csteer_banner_left"] = "16px";
      break;
    case "bottom-center":
      vars["--csteer_banner_bottom"] = "16px";
      vars["--csteer_banner_left"] = "50%";
      vars["--csteer_banner_transform"] = "translateX(-50%)";
      break;

    // --- banner family ----------------------------------------------------
    case "banner-bottom":
    case "bottom-banner":
      vars["--csteer_banner_bottom"] = "0";
      vars["--csteer_banner_left"] = "0";
      vars["--csteer_banner_right"] = "0";
      fullWidthCap();
      break;
    case "banner-top":
    case "top-banner":
      vars["--csteer_banner_top"] = "0";
      vars["--csteer_banner_left"] = "0";
      vars["--csteer_banner_right"] = "0";
      fullWidthCap();
      break;

    // --- popup family -----------------------------------------------------
    case "popup-center":
    case "center-modal":
      vars["--csteer_banner_top"] = "50%";
      vars["--csteer_banner_left"] = "50%";
      vars["--csteer_banner_transform"] = "translate(-50%, -50%)";
      break;

    // --- sidebar family ---------------------------------------------------
    case "sidebar-left":
      vars["--csteer_banner_top"] = "0";
      vars["--csteer_banner_bottom"] = "0";
      vars["--csteer_banner_left"] = "0";
      vars["--csteer_banner_border_radius"] = "0";
      break;
    case "sidebar-right":
      vars["--csteer_banner_top"] = "0";
      vars["--csteer_banner_bottom"] = "0";
      vars["--csteer_banner_right"] = "0";
      vars["--csteer_banner_border_radius"] = "0";
      break;

    // --- pushdown family --------------------------------------------------
    case "pushdown-top":
      vars["--csteer_banner_top"] = "0";
      vars["--csteer_banner_left"] = "0";
      vars["--csteer_banner_right"] = "0";
      fullWidthCap();
      break;
    case "pushdown-bottom":
      vars["--csteer_banner_bottom"] = "0";
      vars["--csteer_banner_left"] = "0";
      vars["--csteer_banner_right"] = "0";
      fullWidthCap();
      break;
  }

  Object.assign(vars, buildMotionVars(settings));

  return vars;
}

/** Shared with banner.ts, which needs the numeric duration to time the
 *  fallback timer that removes the host if `animationend` never fires. */
export const SPEED_MS: Record<string, number> = {
  fast: 120,
  normal: 200,
  slow: 400,
};

/**
 * Travel of the "slide" effect, per position. Edge-anchored formats
 * (banner, sidebar) come in from fully off their own edge — 100% is relative
 * to the element's own size, so it lands exactly flush. The floating formats
 * (box, popup) only need a short nudge; a full-screen sweep on a small card
 * reads as a stunt rather than a transition.
 */
function slideOffset(position: string): { x: string; y: string } {
  switch (position) {
    case "banner-top":
    case "top-banner":
      return { x: "0px", y: "-100%" };
    case "banner-bottom":
    case "bottom-banner":
      return { x: "0px", y: "100%" };
    case "sidebar-left":
      return { x: "-100%", y: "0px" };
    case "sidebar-right":
      return { x: "100%", y: "0px" };
    default:
      // box-* and popup-center: a 16px rise.
      return { x: "0px", y: "16px" };
  }
}

/**
 * Motion variables consumed by the single keyframe pair in styles.css.
 *
 * The pushdown family is pinned to a plain fade whatever the setting says: it
 * physically reflows the host page through a body margin driven by a
 * ResizeObserver, so translating it on top of that makes the whole site jog.
 */
function buildMotionVars(settings: BannerSettings): CssVars {
  const { layout, reopenIcon } = settings;
  const vars: CssVars = {};

  vars["--csteer_transition_duration"] = `${SPEED_MS[layout.animationSpeed ?? "normal"] ?? 200}ms`;

  const isPushdown = layout.position.startsWith("pushdown-");
  const effect =
    isPushdown && layout.animation === "slide" ? "fade" : (layout.animation ?? "slide");

  let x = "0px";
  let y = "0px";
  let scale = "1";
  if (effect === "slide") {
    const off = slideOffset(layout.position);
    x = off.x;
    y = off.y;
  } else if (effect === "zoom") {
    scale = "0.96";
  }
  vars["--csteer_anim_x"] = x;
  vars["--csteer_anim_y"] = y;
  vars["--csteer_anim_scale"] = scale;

  // Floating pill: travels in from whichever corner it is docked to.
  let rx = "0px";
  let ry = "0px";
  if ((reopenIcon?.animation ?? "fade") === "slide") {
    const pos = reopenIcon?.position ?? "bottom-right";
    rx = pos.endsWith("-right") ? "24px" : "-24px";
    ry = pos.startsWith("bottom-") ? "24px" : "-24px";
  }
  vars["--csteer_reopen_anim_x"] = rx;
  vars["--csteer_reopen_anim_y"] = ry;

  return vars;
}
